import { useCallback, useEffect, useRef, useState } from 'react'
import {
  EDGE_SWIPE_FRACTION,
  isWithinLeftEdge,
  resolveDragAxis,
  resolveDragCommit,
} from './dragGesture'
import { prefersReducedMotion } from './scoreCountUp'

/** Render state for the swiped page's wrapper — apply `transform`/`animate` to
 * the element and route its `transitionend` to `onSettled`. */
export interface SwipeBackRenderState {
  phase: 'idle' | 'dragging' | 'settling'
  /** CSS transform for the page wrapper. */
  transform: string
  /** Whether a CSS transition should ease the transform (release only) — false
   *  during the drag itself, which tracks 1:1 with no easing. */
  animate: boolean
  /** True while a committed exit is animating off-screen. */
  committing: boolean
  /** True whenever the finger is actively tracking or settling — lets the
   *  wrapper add its dragging affordance (shadow) only while in motion. */
  active: boolean
}

const IDLE_STATE: SwipeBackRenderState = {
  phase: 'idle',
  transform: 'translateX(0)',
  animate: false,
  committing: false,
  active: false,
}

interface UseSwipeBackOptions {
  /** The page's existing back handler — invoked once a committed back-swipe
   *  finishes sliding off. Additive: on-screen back controls call it too. */
  onBack: () => void
  /** CSS selector for in-page horizontally-scrollable elements (e.g. a chip
   *  carousel); a drag starting inside a match never begins the back gesture. */
  excludeSelector?: string
  /** Fraction of the page width (from the left) a back-swipe must begin inside. */
  edgeFraction?: number
}

/** Edge-swipe-to-go-back for a pushed page. A horizontal drag
 * beginning within the left-edge zone tracks the finger rightward; releasing
 * past the distance-or-velocity threshold slides the page off and invokes
 * `onBack`, a short release springs it back. Shares the axis-lock / velocity /
 * edge primitives with `useDragNav` via `dragGesture.ts`.
 *
 * Attaches native (not React synthetic) touch listeners via `containerRef`
 * because calling preventDefault from a React onTouchMove is unreliable —
 * see https://github.com/facebook/react/issues/9809. Touch-only and additive:
 * mouse/desktop and the on-screen back control are unaffected. */
export function useSwipeBack({
  onBack,
  excludeSelector,
  edgeFraction = EDGE_SWIPE_FRACTION,
}: UseSwipeBackOptions) {
  // A stateful callback ref (rather than a plain useRef) so the touch-listener
  // effect reliably re-attaches after React dev StrictMode's mount/unmount
  // replay, which nulls and re-fires ref callbacks independently of effects.
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const containerRef = useCallback((el: HTMLDivElement | null) => {
    setNode(el)
  }, [])
  const [state, setState] = useState<SwipeBackRenderState>(IDLE_STATE)

  const onBackRef = useRef(onBack)
  useEffect(() => {
    onBackRef.current = onBack
  })

  const rafRef = useRef<number | null>(null)
  const committedRef = useRef(false)
  const gestureRef = useRef<{
    active: boolean
    startX: number
    startY: number
    startTime: number
    axis: 'horizontal' | 'vertical' | null
    dx: number
    reduced: boolean
  } | null>(null)

  // Fired from the wrapper's transitionend once a settle animation completes.
  const handleSettled = useCallback(() => {
    if (committedRef.current) {
      committedRef.current = false
      // The page slid off — hand back to the page's own dismissal, which
      // unmounts this wrapper (no need to reset state it's about to destroy).
      onBackRef.current()
      return
    }
    setState(IDLE_STATE)
  }, [])

  useEffect(() => {
    const el = node
    if (!el) return

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        gestureRef.current = null
        return
      }
      const target = e.target as HTMLElement
      if (excludeSelector && target.closest(excludeSelector)) {
        gestureRef.current = null
        return
      }
      const touch = e.touches[0]
      // Only a drag beginning in the left portion of the page may pop it — the
      // zone is a fraction of the page width so it's easy to grab.
      const rect = el.getBoundingClientRect()
      if (!isWithinLeftEdge(touch.clientX, rect.left, rect.width * edgeFraction)) {
        gestureRef.current = null
        return
      }
      gestureRef.current = {
        active: true,
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        axis: null,
        dx: 0,
        reduced: prefersReducedMotion(),
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      const gesture = gestureRef.current
      if (!gesture || !gesture.active) return
      const touch = e.touches[0]
      if (!touch) return
      const dx = touch.clientX - gesture.startX
      const dy = touch.clientY - gesture.startY

      if (gesture.axis === null) {
        gesture.axis = resolveDragAxis(dx, dy)
        if (gesture.axis === 'vertical') {
          // Let a vertical scroll through untouched.
          gesture.active = false
          return
        }
        if (gesture.axis === null) return
        // Horizontal but pointing left is not a back-swipe — release the
        // gesture rather than hijack it.
        if (dx <= 0) {
          gesture.active = false
          return
        }
      }

      // Horizontal-from-edge intent locked: own the gesture for its remainder.
      e.preventDefault()
      gesture.dx = Math.max(0, dx)
      // Reduced motion: detect the gesture but don't 1:1 track — commit or
      // cancel instantly on release, matching the transition layer's approach.
      if (gesture.reduced) return

      if (rafRef.current !== null) return
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        setState({
          phase: 'dragging',
          transform: `translateX(${gesture.dx}px)`,
          animate: false,
          committing: false,
          active: true,
        })
      })
    }

    const onTouchEnd = () => {
      const gesture = gestureRef.current
      gestureRef.current = null
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      if (!gesture || !gesture.active || gesture.axis !== 'horizontal') return

      const durationMs = Date.now() - gesture.startTime
      const commit = resolveDragCommit(gesture.dx, durationMs)

      if (gesture.reduced) {
        // No 1:1 tracking under reduced motion: commit or cancel with no slide.
        if (commit) onBackRef.current()
        else setState(IDLE_STATE)
        return
      }

      if (commit) {
        committedRef.current = true
        setState({
          phase: 'settling',
          transform: 'translateX(100%)',
          animate: true,
          committing: true,
          active: true,
        })
        return
      }

      committedRef.current = false
      if (gesture.dx === 0) {
        // Never left identity — no transition would fire to settle us.
        setState(IDLE_STATE)
        return
      }
      setState({
        phase: 'settling',
        transform: 'translateX(0)',
        animate: true,
        committing: false,
        active: true,
      })
    }

    const onTouchCancel = () => {
      gestureRef.current = null
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      committedRef.current = false
      setState(IDLE_STATE)
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchCancel, { passive: true })

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [node, excludeSelector, edgeFraction])

  return { containerRef, drag: state, onSettled: handleSettled }
}
